import type { OrderDetails } from '@/entities/order/model'
import type { QuickButton } from '@/entities/state/model'
import { transitionOrders, useAcknowledgeOrder } from '@/entities/order/lib'
import { useOrderStatusMutation } from '@/entities/order/model'
import { useDisplayStateByStatus } from '@/entities/state/model'

interface UseQuickActionsReturn {
  primaryAction: QuickButton | undefined
  handlePrimaryAction: () => Promise<void>
  handleAction: (action: string) => Promise<void>
  statusColor: string | undefined
  isPending: boolean
}

/**
 * Hook to get quick actions for an order.
 * These are presented as buttons on the order card.
 *
 * @param order - The order to get quick actions for
 * @returns An object containing the primary action and handlers for it
 */
export function useQuickActions(order: OrderDetails): UseQuickActionsReturn {
  const { data: quickButtonStates } = useDisplayStateByStatus()
  const { mutateAsync: updateStatus, isPending: isStatusPending } = useOrderStatusMutation()
  const { updateOrderAcknowledgement, isPending: isAcknowledgePending } = useAcknowledgeOrder()
  const isPending = isStatusPending || isAcknowledgePending

  const handleAction = async (action: string) => {
    await transitionOrders([order], action, {
      acknowledge: updateOrderAcknowledgement,
      updateStatus,
    })
  }

  const statusDisplayState = quickButtonStates?.[order.status]
  const primaryAction = statusDisplayState?.quickButtons?.[0]
  const statusColor = statusDisplayState?.color

  const handlePrimaryAction = async () => {
    if (primaryAction?.action) {
      await handleAction(primaryAction.action)
    }
  }

  return {
    primaryAction,
    handlePrimaryAction,
    handleAction,
    statusColor,
    isPending,
  }
}
